import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface ReviewKeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  action: () => void;
  description: string;
  section: string;
}

interface UseReviewKeyboardShortcutsProps {
  onSave?: () => void;
  onComplete?: () => void;
  onBack?: () => void;
  onFocusField?: (fieldName: string) => void;
  onToggleCustomerPanel?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onNextPage?: () => void;
  onPrevPage?: () => void;
}

export function useReviewKeyboardShortcuts({
  onSave,
  onComplete,
  onBack,
  onFocusField,
  onToggleCustomerPanel,
  onZoomIn,
  onZoomOut,
  onNextPage,
  onPrevPage
}: UseReviewKeyboardShortcutsProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const reviewShortcuts: ReviewKeyboardShortcut[] = [
    // Review Actions
    {
      key: 's',
      ctrlKey: true,
      action: () => onSave?.(),
      description: 'Save Progress',
      section: 'Review Actions'
    },
    {
      key: 'Enter',
      ctrlKey: true,
      action: () => onComplete?.(),
      description: 'Complete Review',
      section: 'Review Actions'
    },
    {
      key: 'Escape',
      action: () => onBack?.(),
      description: 'Go Back to Queue',
      section: 'Navigation'
    },

    // PDF Navigation
    {
      key: 'ArrowRight',
      action: () => onNextPage?.(),
      description: 'Next Page',
      section: 'PDF Navigation'
    },
    {
      key: 'ArrowLeft',
      action: () => onPrevPage?.(),
      description: 'Previous Page',
      section: 'PDF Navigation'
    },
    {
      key: '=',
      ctrlKey: true,
      action: () => onZoomIn?.(),
      description: 'Zoom In',
      section: 'PDF Navigation'
    },
    {
      key: '-',
      ctrlKey: true,
      action: () => onZoomOut?.(),
      description: 'Zoom Out',
      section: 'PDF Navigation'
    },

    // Field Navigation
    {
      key: '1',
      altKey: true,
      action: () => onFocusField?.('amount'),
      description: 'Focus Amount Field',
      section: 'Field Navigation'
    },
    {
      key: '2',
      altKey: true,
      action: () => onFocusField?.('date'),
      description: 'Focus Date Field',
      section: 'Field Navigation'
    },
    {
      key: '3',
      altKey: true,
      action: () => onFocusField?.('supplier'),
      description: 'Focus Supplier Field',
      section: 'Field Navigation'
    },
    {
      key: '4',
      altKey: true,
      action: () => onFocusField?.('account'),
      description: 'Focus Account Field',
      section: 'Field Navigation'
    },

    // Panel Controls
    {
      key: 'c',
      ctrlKey: true,
      shiftKey: true,
      action: () => onToggleCustomerPanel?.(),
      description: 'Toggle Customer Panel',
      section: 'Panel Controls'
    },

    // Help
    {
      key: '?',
      shiftKey: true,
      action: () => showReviewShortcuts(),
      description: 'Show Review Shortcuts',
      section: 'Help'
    }
  ];

  const showReviewShortcuts = useCallback(() => {
    const shortcutsBySection = reviewShortcuts.reduce((acc, shortcut) => {
      if (!acc[shortcut.section]) acc[shortcut.section] = [];
      acc[shortcut.section].push(shortcut);
      return acc;
    }, {} as Record<string, ReviewKeyboardShortcut[]>);

    const formatShortcut = (shortcut: ReviewKeyboardShortcut) => {
      const parts = [];
      if (shortcut.ctrlKey) parts.push('Ctrl');
      if (shortcut.metaKey) parts.push('⌘');
      if (shortcut.shiftKey) parts.push('Shift');
      if (shortcut.altKey) parts.push('Alt');
      parts.push(shortcut.key);
      return parts.join(' + ');
    };

    const shortcutList = Object.entries(shortcutsBySection)
      .map(([section, shortcuts]) => 
        `**${section}:**\n` + 
        shortcuts.map(s => `• ${formatShortcut(s)}: ${s.description}`).join('\n')
      ).join('\n\n');

    toast({
      title: "Review Keyboard Shortcuts",
      description: shortcutList,
    });
  }, [toast]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in input fields
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement ||
      (event.target as HTMLElement)?.contentEditable === 'true'
    ) {
      return;
    }

    const shortcut = reviewShortcuts.find(s => {
      const keyMatch = s.key.toLowerCase() === event.key.toLowerCase();
      const ctrlMatch = !!s.ctrlKey === event.ctrlKey;
      const metaMatch = !!s.metaKey === event.metaKey;
      const shiftMatch = !!s.shiftKey === event.shiftKey;
      const altMatch = !!s.altKey === event.altKey;

      return keyMatch && ctrlMatch && metaMatch && shiftMatch && altMatch;
    });

    if (shortcut) {
      event.preventDefault();
      shortcut.action();
    }
  }, [reviewShortcuts, onSave, onComplete, onBack, onFocusField, onToggleCustomerPanel, onZoomIn, onZoomOut, onNextPage, onPrevPage]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const getShortcutsBySection = () => {
    return reviewShortcuts.reduce((sections, shortcut) => {
      if (!sections[shortcut.section]) {
        sections[shortcut.section] = [];
      }
      sections[shortcut.section].push(shortcut);
      return sections;
    }, {} as Record<string, ReviewKeyboardShortcut[]>);
  };

  const formatShortcut = (shortcut: ReviewKeyboardShortcut) => {
    const parts = [];
    if (shortcut.ctrlKey) parts.push('Ctrl');
    if (shortcut.metaKey) parts.push('⌘');
    if (shortcut.shiftKey) parts.push('Shift');
    if (shortcut.altKey) parts.push('Alt');
    parts.push(shortcut.key.toUpperCase());
    return parts.join(' + ');
  };

  return {
    shortcuts: reviewShortcuts,
    getShortcutsBySection,
    formatShortcut,
    showReviewShortcuts
  };
}